/**
 * The add to cart button follows the selected offer:
 * - "1 nail pen": always the normal label.
 * - bundle (3 + 2 free): "N more shade(s)" and a greyer button until 5 shades are chosen, then the normal label.
 *   While shades are missing, tapping a button opens the shade sheet instead of adding to the cart.
 */

const MAIN_BUTTON = '.product-details .product-form-buttons .add-to-cart-button';
// The sticky bar button is a "Shop now" link back to the main button (assets/sticky-shop-now.js), so it is not handled here.
const BUTTONS = MAIN_BUTTON;
const CARD = '.product-details .offer-card';
const MAX = 5;

/** @returns {number} Shades still missing, or 0 when the buttons should behave normally. */
function missingShades() {
  const card = document.querySelector(CARD);
  if (!card) return 0;

  const checked = card.querySelector('input[type="radio"]:checked');
  const picks = card.querySelector('.offer-card__picks');
  if (!(checked instanceof HTMLInputElement) || !(picks instanceof HTMLElement)) return 0;
  if (checked.value === '1') return 0;

  return Math.max(0, MAX - Number(picks.dataset.count ?? 0));
}

/**
 * The element/text node that holds the label of a button.
 * @param {HTMLElement} button
 * @returns {{ get: () => string, set: (value: string) => void } | null}
 */
function labelOf(button) {
  const span = button.querySelector('.add-to-cart-text');
  if (span) {
    return { get: () => (span.textContent ?? '').trim(), set: (value) => (span.textContent = value) };
  }

  const node = [...button.childNodes].find((n) => n.nodeType === Node.TEXT_NODE && (n.textContent ?? '').trim());
  return node ? { get: () => (node.textContent ?? '').trim(), set: (value) => (node.textContent = ` ${value} `) } : null;
}

function update() {
  const missing = missingShades();

  document.querySelectorAll(BUTTONS).forEach((button) => {
    if (!(button instanceof HTMLElement)) return;
    const label = labelOf(button);
    if (!label) return;

    const current = label.get();
    const incompleteLabel = `${missing} more shade(s)`;

    if (missing > 0) {
      // Remember the normal label (the theme may change it, e.g. quantity or price) unless it is ours.
      if (!/more shade\(s\)$/.test(current)) button.dataset.originalLabel = current;
      if (current !== incompleteLabel) label.set(incompleteLabel);
    } else if (/more shade\(s\)$/.test(current)) {
      label.set(button.dataset.originalLabel || 'Add to cart');
    }

    button.classList.toggle('add-to-cart-button--incomplete', missing > 0);
  });
}

// Capture phase: runs before the theme's own click handler on the buttons.
document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest(BUTTONS);
    if (!button || missingShades() === 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const dialog = document.querySelector(`${CARD} dialog.shade-sheet`);
    if (dialog instanceof HTMLDialogElement && !dialog.open) {
      document.documentElement.style.overflow = 'hidden';
      dialog.showModal();
    }
  },
  true
);

document.addEventListener('change', (event) => {
  if (event.target instanceof HTMLInputElement && event.target.type === 'radio' && event.target.closest(CARD)) {
    update();
  }
});

document.addEventListener('offer:shades-changed', update);
document.addEventListener('shade-switcher:updated', update);

// The sticky bar re-renders its label now and then (price, quantity): keep ours in place.
let pending = false;
new MutationObserver(() => {
  if (pending) return;
  pending = true;
  requestAnimationFrame(() => {
    pending = false;
    update();
  });
}).observe(document.documentElement, { childList: true, subtree: true, characterData: true });

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', update);
} else {
  update();
}
